import { Component, computed, inject } from '@angular/core';
import { MenuController } from '@ionic/angular';
import { map, startWith } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { BaseAppComponent, CustomLoader, PurchasingService, SelectTranslations, ThemeService } from 'army-builder-shared';

import { environment } from '../environments/environment';
import pkg from '../../package.json';

import { MenuItem } from './global/menu/menu.component';
import { WarlordUserService } from './settings/user/user.service';
import { SubscriptionService } from './subscription/subscription.service';
import { GAME_IDS, GAME_ROLE_RESTRICTIONS } from './game-id.token';
import { Capacitor } from '@capacitor/core';

@Component({
    standalone: false,
    selector: 'app-root',
    template: `
        <ion-app
            *ngLet="{
                otherClientActive: otherClientActive$ | async,
                hasAccess: subscriptionService.userHasAccess$ | async,
                appPages: appPages$ | async,
                translationState: translationState$ | async,
                isLoggedIn: isLoggedIn$ | async,
                userRoles: userService.user()?.roles || []
            } as props"
            [class]="[platformDescription, config.environment].join(' ')"
        >
            <div class="envWarning" *ngIf="config.environment === 'staging'">Staging environment</div>
            <ion-split-pane contentId="mainRouterOutlet" [disabled]="themeService.isDesktop()">
                <ion-menu type="overlay" side="end" contentId="mainRouterOutlet" *ngIf="props.appPages && !themeService.isDesktop()">
                    <ion-header>
                        <ion-toolbar>
                            <ion-title>{{ 'GLOBAL.MENU' | translate }}</ion-title>
                        </ion-toolbar>
                    </ion-header>
                    <ion-content>
                        <wl-menu
                            style="list"
                            [menuItems]="props.appPages"
                            [roles]="props.userRoles"
                            [isLoggedIn]="props.isLoggedIn"
                            [mode]="'root'"
                        ></wl-menu>
                    </ion-content>
                    <ion-footer>
                        <a href="#" (click)="reportProblem($event)" class="support">{{ 'GLOBAL.SUPPORT.LINK' | translate }}</a>
                        |
                        <a href="mailto:warlord@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                        |
                        <span class="version">{{ appVersion }}</span>
                    </ion-footer>
                </ion-menu>
                <div class="main">
                    <div class="topBar">
                        <img src="/assets/images/warlord.png" alt="Warlord" class="warlordLogo" />
                        <wl-menu
                            [style]="'menu'"
                            [menuItems]="props.appPages"
                            [isLoggedIn]="props.isLoggedIn"
                            [roles]="props.userRoles"
                            [mode]="'root'"
                            [layout]="'horizontal'"
                        ></wl-menu>
                    </div>

                    <ion-router-outlet id="mainRouterOutlet" [animation]="null"></ion-router-outlet>

                    <div class="footer">
                        <a href="#" (click)="reportProblem($event)" class="support">{{ 'GLOBAL.SUPPORT.LINK' | translate }}</a>
                        |
                        <a href="mailto:warlord@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                        |
                        <a class="version" [routerLink]="'/pages/patch-notes'">{{ appVersion }}</a>
                    </div>
                </div>
            </ion-split-pane>

            <div class="fullPageOverlay" *ngIf="supported === false">
                <ion-card>
                    <h1>{{ 'GLOBAL.UNSUPPORTED_CLIENT.TITLE' | translate }}</h1>
                    <p>
                        {{
                            'GLOBAL.UNSUPPORTED_CLIENT.MESSAGE'
                                | translate
                                    : {
                                          minClientVersion: this.minClientVersion,
                                          currentClientVersion: this.currentClientVersion
                                      }
                        }}
                    </p>
                </ion-card>
            </div>

            <div class="useHere" *ngIf="props.otherClientActive">
                <ion-card>
                    <h1>{{ 'GLOBAL.UNSUPPORTED_CLIENT.TITLE' | translate }}</h1>
                    <p>
                        {{
                            'GLOBAL.UNSUPPORTED_CLIENT.MESSAGE'
                                | translate
                                    : {
                                          minClientVersion: this.minClientVersion,
                                          currentClientVersion: this.currentClientVersion
                                      }
                        }}
                    </p>
                </ion-card>
            </div>

            <abs-entity-preload-modal [gameIds]="gameIds()"></abs-entity-preload-modal>
            <abs-translation-preload-modal [gameIds]="gameIds()" [language]="language"></abs-translation-preload-modal>
        </ion-app>
    `,
    styles: [
        `
            .staging .envWarning {
                background: #ffcc00;
                color: black;
                font-weight: bold;
                position: absolute;
                top: 0;
                left: 0;
                z-index: 1000;
                width: 100%;
                height: 40px;
                line-height: 40px;
                text-align: center;
                font-family: 'Courier New', Courier, monospace;
            }

            .staging ion-split-pane {
                margin-top: 40px;
            }

            .logo {
                max-width: 200px;
            }

            ion-footer {
                padding: 5px;
                background: #5b5b5b;
                color: white;
            }

            ion-footer a {
                color: white;
            }

            .main {
                width: 100%;
            }

            .version,
            .contact,
            .support {
                padding: 0 10px;
            }

            abs-entity-preload-modal,
            abs-translation-preload-modal {
                position: relative;
                z-index: 1;
            }

            @media screen and (max-width: 991px) {
                .topBar,
                .footer {
                    display: none;
                }

                ion-menu wl-menu {
                    display: block;
                }
            }

            @media screen and (min-width: 992px) {
                .staging ion-split-pane {
                    margin-top: 0;
                    padding-top: 40px;
                }

                .topBar {
                    background: black;
                    display: flex;
                    width: 100%;
                    height: 50px;
                    position: relative;
                    z-index: 2;
                }

                .topBar .warlordLogo {
                    height: 32px;
                    margin: 9px 20px 9px 9px;
                }

                .topBar wl-menu {
                    flex: 1 1 auto;
                }

                ion-menu {
                    display: none;
                }

                .footer {
                    background: #232323;
                    position: relative;
                    padding: 10px;
                    color: white;
                    text-align: center;
                    font-size: 12px;
                    z-index: 10;
                }
                .footer a {
                    color: #cfcfcf;
                    text-decoration: none;
                }
                .footer a:hover {
                    color: white;
                    transition:
                        all ease 0.3s,
                        text-decoration 0.1s ease;
                    text-decoration: underline;
                }

                .main {
                    display: flex;
                    flex-direction: column;
                    min-height: 100vh;
                }

                .staging .main {
                    min-height: calc(100vh - 40px);
                }

                ion-router-outlet {
                    flex: 1 1 auto;
                    position: relative;
                    z-index: 1;
                }
            }
        `
    ]
})
export class AppComponent extends BaseAppComponent {
    affId = environment.affiliateId;
    appVersion: string = pkg.version;
    clearDataBeforeVersion: string = pkg.clearDataBeforeVersion;
    supported = null;
    minClientVersion = null;
    currentClientVersion = null;
    otherClientActive$ = new BehaviorSubject<boolean>(false);
    translationState$ = this.store.select(SelectTranslations);
    isLoggedIn$ = this.userService.loggedIn$;
    themeService = inject(ThemeService);

    gameIds = computed(() => {
        const roles = this.userService.user()?.roles || [];
        return ['global', ...Object.values(GAME_IDS)].filter(
            (id) => !GAME_ROLE_RESTRICTIONS[id] || roles.includes(GAME_ROLE_RESTRICTIONS[id])
        );
    });
    platformDescription = Capacitor.getPlatform();

    MENU_GAMES: MenuItem[] = [
        // {
        //     title: 'GLOBAL.MENU_ITEMS.BOLT_ACTION',
        //     children: [
        //         { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/bolt-action/forces' },
        //         { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/bolt-action/library' },
        //         { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/bolt-action/settings' }
        //     ],
        //     requiresLogin: true
        // },
        {
            title: 'GLOBAL.MENU_ITEMS.BOLT_ACTION_3E',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/bolt-action-3e/forces' },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/bolt-action-3e/library' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/bolt-action-3e/settings' }
            ],
            requiresLogin: true,
            requiredRoles: []
        },
        {
            title: 'GLOBAL.MENU_ITEMS.K47',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/k47/forces' },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/k47/library' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/k47/settings' }
            ],
            requiresLogin: true,
            requiredRoles: ['k47-early-access']
        },
        {
            title: 'GLOBAL.MENU_ITEMS.BLACK_SEAS',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/black-seas/forces' },
                { title: 'GLOBAL.MENU_ITEMS.BATTLE_TRACKER', icon: 'list', url: '/black-seas/battle' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/black-seas/settings' }
            ],
            requiresLogin: true,
            requiredRoles: []
        },
        {
            title: 'GLOBAL.MENU_ITEMS.BLOOD_RED_SKIES',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/blood-red-skies/forces' },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/blood-red-skies/library' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/blood-red-skies/settings' }
            ],
            requiresLogin: true,
            requiredRoles: []
        }
    ];

    MENU_OTHER: MenuItem[] = [
        {
            title: 'GLOBAL.MENU_ITEMS.SETTINGS',
            icon: 'cog',
            url: '/settings',
            requiresLogin: true
        },
        {
            title: 'GLOBAL.MENU_ITEMS.SUPPORT_TICKETS',
            icon: 'flag-outline',
            url: '/support',
            requiresLogin: true
        },
        {
            title: 'GLOBAL.MENU_ITEMS.PATCH_NOTES',
            icon: 'list',
            url: '/pages/patch-notes'
        },
        {
            title: 'GLOBAL.MENU_ITEMS.KNOWN_ISSUES',
            icon: 'bug-outline',
            url: '/pages/known-issues'
        },
        {
            title: 'GLOBAL.MENU_ITEMS.FAQ',
            icon: 'help-circle-outline',
            url: '/pages/faq'
        },
        {
            title: 'GLOBAL.MENU_ITEMS.EULA',
            icon: 'reader-outline',
            url: '/eula'
        }
    ];

    public appPages$: Observable<MenuItem[]> = combineLatest([
        this.userService.isAdmin$,
        this.subscriptionService.userHasAccess$,
        this.userService.loggedIn$
    ]).pipe(
        // select(LoginSelector),
        map(([isAdmin, isSubscriber, isLoggedIn]) => {
            let menuItems: MenuItem[] = [...this.MENU_OTHER];

            if (isSubscriber) {
                menuItems = [
                    ...this.MENU_GAMES,
                    {
                        title: 'GLOBAL.MENU_ITEMS.OTHER',
                        children: this.MENU_OTHER
                    }
                ];
            }

            menuItems.push({
                title: '',
                placeholder: true
            });

            if (isAdmin) {
                menuItems.push({
                    title: 'GLOBAL.MENU_ITEMS.ADMIN',
                    icon: 'create',
                    url: '/admin'
                });

                // menuItems[0].children?.push({
                //     title: 'GLOBAL.MENU_ITEMS.COLLECTION_MANAGER',
                //     icon: 'list',
                //     url: '/bolt-action-3e/collection-manager'
                // });
            }

            if (isLoggedIn) {
                menuItems.push({
                    title: 'GLOBAL.LOGIN.BUTTONS.LOGOUT',
                    icon: 'log-out-outline',
                    onClick: () => this.userService.logout()
                });
            }
            return menuItems;
        })
    );

    constructor(
        protected userService: WarlordUserService,
        protected menu: MenuController,
        protected subscriptionService: SubscriptionService,
        protected purchaseService: PurchasingService,
        protected customLoader: CustomLoader
    ) {
        super();
        this.userService.loggedIn$.subscribe((loggedIn) => {
            this.menu.enable(loggedIn);
        });
    }

    reportProblem(e?) {
        if (e) {
            e.preventDefault();
        }
        this.supportService.showModal();
    }

    init() {}
}
